<template #default>
    <form
        v-if="status.detail"
        @submit.prevent="candidateStatusUUID ? requestUpdate() : requestCreate()"
    >
        <b-form-group
            :label="$t('label.name')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.name"
                :class="{ 'is-invalid': submitted && !$v.createForm.name.required }"
                autocomplete="off"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required" class="text-right">
                {{ $t("message.requiredX", [$t("label.name")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('label.description')"
            label-cols="4"
            label-align="left"
        >
            <b-form-textarea
                v-model.trim="createForm.description"
            ></b-form-textarea>
        </b-form-group>
        <b-form-group
            label="Create User"
            label-cols="4"
            label-align="left"
        >
            <b-form-checkbox
                v-model="createForm.isCreateUser"
                switch
                class="mt-2"
            />
        </b-form-group>
        <b-form-group
            label="Email"
            label-cols="4"
            label-align="left"
        >
            <b-form-checkbox
                v-model="createForm.isEmail"
                switch
                class="mt-2"
            />
        </b-form-group>
        <div
            v-if="createForm.isEmail"
            class="fade-in"
        >
            <b-form-group
                label="Email Subject"
                label-cols="4"
                label-align="left"
            >
                <b-form-input
                    v-model.trim="createForm.emailSubject"
                    autocomplete="off"
                />
            </b-form-group>
            <b-form-group
                label="Email Content"
                label-cols="4"
                label-align="left"
            >
                <ckeditor
                    v-model="createForm.emailContent"
                    :editor="editor"
                    :config="editorConfig"
                />
            </b-form-group>
            <b-form-group
                label="Email CC"
                label-cols="4"
                label-align="left"
            >
                <multiselect
                    v-model="createForm.emailCc"
                    :options="[]"
                    :multiple="true"
                    :taggable="true"
                    :placeholder="$t('message.pressEnterToAddX', [])"
                    :tag-placeholder="$t('message.pressEnterToAddX', ['Email'])"
                    :show-options="false"
                    :show-no-options="false"
                    v-on:tag="addEmailCc"
                />
            </b-form-group>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">
                    {{ candidateStatusUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Multiselect from "vue-multiselect";
import i18n from "@/i18n";
import { alertHelper } from "@/utils";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { candidateStatusMethods } from "@/state/api";
import { CANDIDATE_STATUS_MODULE } from "@/state/api/candidateStatus.state";

export default {
    props: {
        candidateStatusUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                name: { required },
            },
        };
    },
    components: { Multiselect, ckeditor: CKEditor.component },
    async created() {
        await this.getCandidateStatusData();
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            editor: ClassicEditor,
            editorConfig: {
                removePlugins: [
                    'ImageCaption'
                ],
                toolbar: [
                    'selectAll',
                    'heading',
                    'bold',
                    'italic',
                    'blockQuote',
                    'bulletedList',
                    'numberedList',
                    'undo',
                    'redo',
                    'outdent',
                    'indent',
                    // 'imageUpload',
                    // 'imageStyle:full',
                    // 'imageStyle:side',
                    // 'imageTextAlternative',
                ],
            },
        };
    },
    methods: {
        ...candidateStatusMethods,
        defaultForm() {
            return {
                uuid: "",
                name: "",
                description: "",
                emailSubject: "",
                emailContent: "",
                emailCc: [],
                isEmail: false,
                isCreateUser: false,
            };
        },
        async getCandidateStatusData() {
            if (this.candidateStatusUUID) {
                await this.getCandidateStatus({ uuid: this.candidateStatusUUID })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                name: this.data.name,
                                description: this.data.description,
                                emailSubject: this.data.emailSubject,
                                emailContent: this.data.emailContent || "",
                                emailCc: this.data.emailCc ?? [],
                                isEmail: this.data.isEmail ? true : false,
                                isCreateUser: this.data.isCreateUser ? true : false,
                            };
                        }
                    });
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    emailContent: this.createForm.emailContent || "",
                    isEmail: this.createForm.isEmail ? 1 : 0,
                    isCreateUser: this.createForm.isCreateUser ? 1 : 0,
                };
                this.createCandidateStatus({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    emailContent: this.createForm.emailContent || "",
                    isEmail: this.createForm.isEmail ? 1 : 0,
                    isCreateUser: this.createForm.isCreateUser ? 1 : 0,
                };
                this.updateCandidateStatus({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        addEmailCc(newTag) {
            const tag = newTag.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (tag && emailRegex.test(tag) && !this.createForm.emailCc.includes(tag)) this.createForm.emailCc.push(tag);
            else alertHelper.alertWarning({ message: "Invalid email", title: i18n.t('message.warning') });
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(CANDIDATE_STATUS_MODULE, ['status']),
        data() {
            return this.$store.state.api.candidateStatus.details;
        },
    },
}
</script>

